import React, { useState, useEffect, useRef } from 'react';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import { toWords } from 'number-to-words';
import { v4 as uuidv4 } from 'uuid';
import { QRCodeCanvas } from 'qrcode.react';
import './Invoice.css'; // Make sure you have the CSS additions

// --- Constants ---
const ANANTA_INFO = {
    logoUrl: 'https://res.cloudinary.com/ddkyeuhk8/image/upload/v1744867815/anantainfotech/ff8b3ol8tddtfgxvvgsw.png',
    companyName: 'ANANTA INFOTECH',
    address: 'Dineshpur Udham Singh Nagar, Uttarakhand(263160)',
    contactNo: '7533928185',
    email: 'kishor002912@gmail.com',
    bank: {
        holderName: 'Kishor Kumar Sarkar',
        bankName: 'BANK OF BARODA',
        accountNo: '37930100008116',
        ifsc: 'BARB0DINESH',
        upi: '7533928185@okbizaxis',
        branch: 'DINESHPUR',
    },
};

const LOCAL_STORAGE_KEY = 'anantaInvoices_v2';
const RUPEE_SYMBOL = '₹';

// --- Helper Functions --- (Keep as they are)
const formatCurrency = (amount) => {
     const num = Number(amount || 0);
     return `${RUPEE_SYMBOL} ${num.toFixed(2)}`;
};

const sanitizeFilename = (name) => {
    if (!name) return 'Invoice';
    return name.replace(/[\\/:*?"<>|]/g, '_').trim() || 'Invoice';
};

// --- Component ---
function Invoice() {
    // --- State Variables --- (Keep as they are)
    const [invoiceId, setInvoiceId] = useState(`INV-${Date.now().toString().slice(-6)}`);
    const [invoiceNumber, setInvoiceNumber] = useState('');
    const [invoiceDate, setInvoiceDate] = useState(new Date().toISOString().slice(0, 10));
    const [clientName, setClientName] = useState('');
    const [clientAddress, setClientAddress] = useState('');
    const [clientGstin, setClientGstin] = useState('');
    const [clientState, setClientState] = useState('');
    const [lineItems, setLineItems] = useState([
        { id: uuidv4(), description: '', sac: '', quantity: '1', rate: '' },
    ]);
    const [cgstRate, setCgstRate] = useState('');
    const [sgstRate, setSgstRate] = useState('');
    const [igstRate, setIgstRate] = useState('');
    const [advancePayment, setAdvancePayment] = useState('');
    const [savedInvoices, setSavedInvoices] = useState([]);
    const invoiceRef = useRef();

    // --- Effects --- (Keep as they are)
    useEffect(() => {
        const storedInvoices = localStorage.getItem(LOCAL_STORAGE_KEY);
        if (storedInvoices) {
            try {
                setSavedInvoices(JSON.parse(storedInvoices));
            } catch (error) {
                console.error("Error parsing saved invoices:", error);
                localStorage.removeItem(LOCAL_STORAGE_KEY);
            }
        }
    }, []);

    useEffect(() => {
        localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(savedInvoices));
    }, [savedInvoices]);

    // --- Calculations --- (Keep as they are)
    const totalAmountBeforeTax = lineItems.reduce((sum, item) => {
        const quantity = Number(item.quantity || 0);
        const rate = Number(item.rate || 0);
        return sum + (quantity * rate);
    }, 0);
    const cgstAmount = totalAmountBeforeTax * (Number(cgstRate || 0) / 100);
    const sgstAmount = totalAmountBeforeTax * (Number(sgstRate || 0) / 100);
    const igstAmount = totalAmountBeforeTax * (Number(igstRate || 0) / 100);
    const totalTaxAmount = cgstAmount + sgstAmount + igstAmount;
    const grandTotal = totalAmountBeforeTax + totalTaxAmount;
    const amountDue = grandTotal - Number(advancePayment || 0);
    const grandTotalForWords = parseFloat(grandTotal.toFixed(2));
    const amountInWords = grandTotalForWords > 0
        ? toWords(grandTotalForWords)
            .split(' ')
            .map(word => word.charAt(0).toUpperCase() + word.slice(1))
            .join(' ')
        : 'Zero';
    const upiUri = ANANTA_INFO.bank.upi
        ? `upi://pay?pa=${ANANTA_INFO.bank.upi}&pn=${encodeURIComponent(ANANTA_INFO.bank.holderName)}&am=${amountDue > 0 ? amountDue.toFixed(2) : ''}&cu=INR`
        : null;

    // --- Event Handlers --- (Keep as they are)
    const handleInputChange = (setter) => (event) => setter(event.target.value);
    const handleNumericInputChange = (setter) => (event) => {
        const value = event.target.value;
        if (value === '' || /^\d*\.?\d*$/.test(value)) setter(value);
    };
    const handleIntegerInputChange = (setter) => (event) => {
        const value = event.target.value;
        if (value === '' || /^[1-9]\d*$/.test(value)) setter(value);
        else if (value === '0') setter('');
    };
    const handleLineItemChange = (id, field) => (event) => {
         const value = event.target.value;
         setLineItems(prevItems =>
             prevItems.map(item => {
                 if (item.id === id) {
                     if ((field === 'rate') && !(value === '' || /^\d*\.?\d*$/.test(value))) return item;
                     if ((field === 'quantity') && !(value === '' || /^[1-9]\d*$/.test(value))) {
                          if (value === '0') return {...item, [field]: ''};
                          return item;
                     }
                     return { ...item, [field]: value };
                 }
                 return item;
             })
         );
     };
    const addLineItem = () => setLineItems([...lineItems, { id: uuidv4(), description: '', sac: '', quantity: '1', rate: '' }]);
    const removeLineItem = (idToRemove) => setLineItems(prevItems => prevItems.filter(item => item.id !== idToRemove));

    // --- Save/Load/Delete/Reset Functions --- (Keep as they are)
    const handleSaveInvoice = () => {
        const newInvoiceData = {
            id: invoiceId, savedAt: new Date().toISOString(),
            invoiceNumber, invoiceDate, clientName, clientAddress, clientGstin, clientState,
            lineItems, cgstRate, sgstRate, igstRate, advancePayment,
        };
         setSavedInvoices(prevInvoices => {
            const existingIndex = prevInvoices.findIndex(inv => inv.id === invoiceId);
            if (existingIndex > -1) {
                const updatedInvoices = [...prevInvoices];
                updatedInvoices[existingIndex] = newInvoiceData;
                return updatedInvoices;
            } else {
                return [...prevInvoices, newInvoiceData];
            }
        });
        alert('Invoice saved!');
    };
    const handleLoadInvoice = (invoiceToLoad) => {
        if (!invoiceToLoad) return;
        setInvoiceId(invoiceToLoad.id);
        setInvoiceNumber(invoiceToLoad.invoiceNumber || '');
        setInvoiceDate(invoiceToLoad.invoiceDate || new Date().toISOString().slice(0, 10));
        setClientName(invoiceToLoad.clientName || '');
        setClientAddress(invoiceToLoad.clientAddress || '');
        setClientGstin(invoiceToLoad.clientGstin || '');
        setClientState(invoiceToLoad.clientState || '');
        setLineItems(invoiceToLoad.lineItems?.map(item => ({
                id: item.id || uuidv4(), description: item.description || '',
                sac: item.sac || '', quantity: item.quantity || '1',
                rate: item.rate || ''
        })) || [{ id: uuidv4(), description: '', sac: '', quantity: '1', rate: '' }]);
        setCgstRate(invoiceToLoad.cgstRate || '');
        setSgstRate(invoiceToLoad.sgstRate || '');
        setIgstRate(invoiceToLoad.igstRate || '');
        setAdvancePayment(invoiceToLoad.advancePayment || '');
        alert(`Loaded invoice for ${invoiceToLoad.clientName || 'N/A'}`);
    };
     const handleDeleteInvoice = (idToDelete) => {
         if (window.confirm('Are you sure you want to delete this saved invoice?')) {
             setSavedInvoices(prevInvoices => prevInvoices.filter(inv => inv.id !== idToDelete));
             if (invoiceId === idToDelete) resetForm();
         }
     };
    const resetForm = () => {
        setInvoiceId(`INV-${Date.now().toString().slice(-6)}`);
        setInvoiceNumber('');
        setInvoiceDate(new Date().toISOString().slice(0, 10));
        setClientName(''); setClientAddress(''); setClientGstin(''); setClientState('');
        setLineItems([{ id: uuidv4(), description: '', sac: '', quantity: '1', rate: '' }]);
        setCgstRate(''); setSgstRate(''); setIgstRate('');
        setAdvancePayment('');
    };

    // --- PDF Download --- (Keep as is, crossOrigin added helps here)
    const handleDownloadPdf = () => {
        const input = invoiceRef.current;
        if (!input) return;
        const controlButtons = document.querySelectorAll('.controls > button');
        controlButtons.forEach(btn => btn.style.visibility = 'hidden');
        const actionElements = input.querySelectorAll('.action-column-header, .action-cell');
        actionElements.forEach(el => el.style.display = 'none');

        // Hide elements specifically marked with no-print during PDF generation
        const noPrintElements = input.querySelectorAll('.no-print');
        noPrintElements.forEach(el => el.style.display = 'none');


        html2canvas(input, {
            scale: 2, useCORS: true, logging: true,
            onclone: (document) => { }
        })
        .then((canvas) => {
            const imgData = canvas.toDataURL('image/png');
            const pdf = new jsPDF('p', 'mm', 'a4');
            const pdfWidth = pdf.internal.pageSize.getWidth();
            const pdfHeight = pdf.internal.pageSize.getHeight();
            const margin = 10;
            const imgProps = pdf.getImageProperties(imgData);
            const imgWidth = pdfWidth - 2 * margin;
            const imgHeight = (imgProps.height * imgWidth) / imgProps.width;
            let heightLeft = imgHeight;
            let position = margin;

            pdf.addImage(imgData, 'PNG', margin, position, imgWidth, imgHeight);
            heightLeft -= (pdfHeight - 2 * margin);

            while (heightLeft > 0) {
                position = heightLeft - imgHeight + margin;
                pdf.addPage();
                pdf.addImage(imgData, 'PNG', margin, position, imgWidth, imgHeight);
                heightLeft -= (pdfHeight - 2 * margin);
            }

            const filename = sanitizeFilename(clientName || invoiceNumber || 'Invoice');
            pdf.save(`${filename}.pdf`);

            controlButtons.forEach(btn => btn.style.visibility = 'visible');
            actionElements.forEach(el => el.style.display = '');
            noPrintElements.forEach(el => el.style.display = ''); // Restore display


        })
        .catch(err => {
            console.error("Error generating PDF:", err);
            controlButtons.forEach(btn => btn.style.visibility = 'visible');
            actionElements.forEach(el => el.style.display = '');
            noPrintElements.forEach(el => el.style.display = ''); // Restore display on error
             alert("Error generating PDF. Check console for details.");
        });
    };


    // --- Render ---
    return (
        <div className="invoice-app">
            <h1>Invoice Generator</h1>

             {/* --- CONTROLS --- */}
             {/* ... unchanged ... */}


             {/* --- SAVED INVOICES LIST --- */}
            {savedInvoices.length > 0 && (
                <div className="saved-invoices no-print">
                   {/* ... unchanged ... */}
                     <h2>Saved Invoices</h2>
                     <ul>
                        {savedInvoices.sort((a, b) => new Date(b.savedAt) - new Date(a.savedAt)).map((inv) => (
                            <li key={inv.id}>
                                <span>
                                    {new Date(inv.savedAt).toLocaleDateString()} -
                                    Inv #: {inv.invoiceNumber || 'N/A'} -
                                    Client: {inv.clientName || 'N/A'}
                                </span>
                                <div>
                                    <button onClick={() => handleLoadInvoice(inv)}>Load</button>
                                    <button className="delete-button" onClick={() => handleDeleteInvoice(inv.id)}>Delete</button>
                                </div>
                            </li>
                        ))}
                    </ul>
                </div>
            )}


             {/* --- INVOICE VIEW (Printable Area) --- */}
            <div className="invoice-container" ref={invoiceRef}>
                {/* --- Header --- */}
                {/* ... unchanged ... */}
                 <div className="invoice-header">
                   <div className="logo-container">
                        <img src={ANANTA_INFO.logoUrl} alt="Ananta Infotech Logo" className="logo" crossOrigin="anonymous"/>
                        <span className="company-name-header">{ANANTA_INFO.companyName}</span>
                    </div>
                    <div className="invoice-title">INVOICE</div>
                </div>

                {/* --- Details Section --- */}
                {/* ... unchanged ... */}
                <div className="details-section">
                     <div className="company-details-inv">
                        <label>Invoice No.:</label>
                        <input type="text" value={invoiceNumber} onChange={handleInputChange(setInvoiceNumber)} placeholder="e.g., 2024/001" />
                        <label>Invoice Date:</label>
                        <input type="date" value={invoiceDate} onChange={handleInputChange(setInvoiceDate)} />
                        <label>State:</label>
                        <span>Uttarakhand</span>
                    </div>
                    <div className="client-details-inv">
                        <strong>Bill To Party:</strong>
                        <label>Name:</label>
                        <input type="text" value={clientName} onChange={handleInputChange(setClientName)} placeholder="Client Name" />
                        <label>Address:</label>
                        <textarea value={clientAddress} onChange={handleInputChange(setClientAddress)} placeholder="Client Address"></textarea>
                        <label>GSTIN:</label>
                        <input type="text" value={clientGstin} onChange={handleInputChange(setClientGstin)} placeholder="Client GSTIN (Optional)" />
                         <label>State:</label>
                         <input type="text" value={clientState} onChange={handleInputChange(setClientState)} placeholder="Client State" />
                    </div>
                </div>


                {/* --- Line Items Table --- */}
                 {/* ... unchanged ... */}
                 <div className="items-section">
                    <table>
                        <thead>
                            <tr>
                                <th className="sno-col">S.No.</th>
                                <th className="desc-col">Service Description</th>
                                <th className="sac-col">SAC Code</th>
                                <th className="qty-col">Quantity</th>
                                <th className="rate-col">Rate ({RUPEE_SYMBOL})</th>
                                <th className="amount-col">Total Amount ({RUPEE_SYMBOL})</th>
                                <th className='action-column-header no-print'>Action</th> {/* Add no-print */}
                            </tr>
                        </thead>
                        <tbody>
                            {lineItems.map((item, index) => {
                                const quantity = Number(item.quantity || 0);
                                const rate = Number(item.rate || 0);
                                const lineTotal = quantity * rate;
                                return (
                                <tr key={item.id}>
                                    <td className="sno-col">{index + 1}</td>
                                    <td className="desc-col"><input type="text" value={item.description} onChange={handleLineItemChange(item.id, 'description')} placeholder="Service Details" /></td>
                                    <td className="sac-col"><input type="text" value={item.sac} onChange={handleLineItemChange(item.id, 'sac')} placeholder="SAC" /></td>
                                    <td className="qty-col">
                                         <input
                                             type="text" // Use text for validation flexibility
                                             value={item.quantity}
                                             onChange={handleLineItemChange(item.id, 'quantity')}
                                             placeholder="1"
                                             className="quantity-input"
                                         />
                                     </td>
                                    <td className="rate-col">
                                         <input
                                             type="text" // Use text for validation flexibility
                                             value={item.rate}
                                             onChange={handleLineItemChange(item.id, 'rate')}
                                             placeholder="0.00"
                                             className="rate-input"
                                         />
                                     </td>
                                    <td className="amount-col">{formatCurrency(lineTotal)}</td>
                                    <td className='action-cell no-print'> {/* Add no-print */}
                                        {lineItems.length > 1 && (
                                            <button className="remove-item-btn" onClick={() => removeLineItem(item.id)} title="Remove Item"></button>
                                        )}
                                    </td>
                                </tr>
                                );
                            })}
                        </tbody>
                    </table>
                    <button onClick={addLineItem} className="add-item-btn no-print">+ Add Service</button>
                </div>


                 {/* --- Totals Section --- */}
                 {/* ... unchanged ... */}
                 <div className="summary-section">
                    <div className="amount-words">
                        <strong>Grand Total in words:</strong>
                        <p>Rupees {amountInWords} Only/-</p>
                    </div>
                    <div className="totals-table">
                        <table>
                            <tbody>
                                <tr><td>Sub Total</td><td>{formatCurrency(totalAmountBeforeTax)}</td></tr>
                                <tr className="tax-row">
                                     <td> Add: CGST @ <input type="text" value={cgstRate} onChange={handleNumericInputChange(setCgstRate)} placeholder="0" className="tax-rate-input"/> % </td>
                                     <td>{formatCurrency(cgstAmount)}</td>
                                 </tr>
                                 <tr className="tax-row">
                                     <td> Add: SGST @ <input type="text" value={sgstRate} onChange={handleNumericInputChange(setSgstRate)} placeholder="0" className="tax-rate-input"/> % </td>
                                     <td>{formatCurrency(sgstAmount)}</td>
                                 </tr>
                                 <tr className="tax-row">
                                     <td> Add: IGST @ <input type="text" value={igstRate} onChange={handleNumericInputChange(setIgstRate)} placeholder="0" className="tax-rate-input"/> % </td>
                                     <td>{formatCurrency(igstAmount)}</td>
                                 </tr>
                                <tr><td>Total Tax amount</td><td>{formatCurrency(totalTaxAmount)}</td></tr>
                                <tr>
                                     <td>Advance payment</td>
                                     <td> <span className="currency-symbol-input">{RUPEE_SYMBOL}</span> <input type="text" value={advancePayment} onChange={handleNumericInputChange(setAdvancePayment)} placeholder="0.00" className="advance-input"/> </td>
                                 </tr>
                                <tr className="grand-total"><td>Total Amount Due</td><td>{formatCurrency(amountDue)}</td></tr>
                            </tbody>
                        </table>
                    </div>
                </div>


                {/* --- Footer Section (MODIFIED) --- */}
                 <div className="footer-section">
                    <div className="bank-details">
                        <strong>Bank Details:</strong>
                        <p>A/c Holder's Name: <span>{ANANTA_INFO.bank.holderName}</span></p>
                        <p>Bank Name: <span>{ANANTA_INFO.bank.bankName}</span></p>
                        <p>A/c Number: <span>{ANANTA_INFO.bank.accountNo}</span></p>
                        <p>IFSC Code: <span>{ANANTA_INFO.bank.ifsc}</span></p>
                        <p>Paytm UPI: <span>{ANANTA_INFO.bank.upi}</span></p>
                        <p>Bank Branch: <span>{ANANTA_INFO.bank.branch}</span></p>
                     </div>

                     {upiUri && (
                         <div className="qr-code-area">
                            <strong>Scan to Pay (UPI):</strong>
                             <div className="qr-code-container">
                                 <QRCodeCanvas value={upiUri} size={110} level={"L"} includeMargin={false} />
                             </div>
                             <span className='qr-upi-id'>{ANANTA_INFO.bank.upi}</span>
                         </div>
                     )}

                    {/* --- SIGNATURE AREA (MODIFIED) --- */}
                    <div className="signature-area">
                        <p>For {ANANTA_INFO.companyName}</p>
                        {/* Removed the empty signature-space div */}
                        <img
                            src="https://res.cloudinary.com/ddkyeuhk8/image/upload/v1744876155/anantainfotech/mtsgrlircmttgycbbey6.svg"
                            alt="Digital Signature"
                            className="digital-signature-img" // Added class
                            crossOrigin="anonymous" // Important for html2canvas
                        />
                        <p className="digital-signature-note">(Digitally Signed)</p> {/* Added the note */}
                    </div>
                    {/* --- END SIGNATURE AREA --- */}

                 </div>

                 <div className="original-copy">
                    ORIGINAL FOR RECIPIENT
                 </div>

               
                 <div className="company-footer-info">
                     <hr className="footer-separator" />
                     <p>{ANANTA_INFO.companyName}, {ANANTA_INFO.address}</p>
                     <p>Contact No: {ANANTA_INFO.contactNo} | Email: {ANANTA_INFO.email}</p>
                 </div>


            </div> 

       
            <div className="controls no-print">
                 <button onClick={handleSaveInvoice}>Save Current Invoice</button>
                 <button onClick={handleDownloadPdf}>Download as PDF</button>
                 <button onClick={resetForm}>New Invoice / Reset Form</button>
            </div>

        </div> 
    );
}

export default Invoice;